/**
 * Audit API Module
 *
 * This module provides functions for interacting with audit log-related endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";
import { HoldingAudit, TransactionAudit } from "@/types";

/**
 * Retrieves transaction audit history for a specific transaction.
 *
 * @param transactionId - The ID of the transaction to get audit history for
 * @returns A promise that resolves to an array of TransactionAudit objects.
 * If the request fails, returns an empty array.
 */
export const getTransactionHistory = async (
  transactionId: number
): Promise<TransactionAudit[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/audit/transaction/${transactionId}`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log(
        "Failed to retrieve transaction history",
        response.statusText
      );
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting transaction history:", error);
  }

  return [];
};

/**
 * Retrieves holding audit history for a specific holding.
 *
 * @param holdingId - The ID of the holding to get audit history for
 * @returns A promise that resolves to an array of HoldingAudit objects.
 * If the request fails, returns an empty array.
 */
export const getHoldingHistory = async (
  holdingId: number
): Promise<HoldingAudit[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/audit/holding/${holdingId}`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve holding history", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting holding history:", error);
  }

  return [];
};
